import React, { useCallback, useMemo, useState } from 'react';

import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import {
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';

import { CatalogRoom, ResolvedRoomMatch, RoomMatchResult, StopsellExtraction } from '~/types/stopsell';

import { matchRoomsToCatalog } from '~/utils/stopsellRoomMatching';

import RoomMatchRow from './RoomMatchRow';

interface Props {
  open: boolean;
  onClose: () => void;
  onContinue: (resolvedRooms: Array<ResolvedRoomMatch>) => void;
  extraction: StopsellExtraction;
  catalogRooms: Array<CatalogRoom>;
  fileName: string;
  isBlackoutLoading: boolean;
}

export default function StopsellRoomMatchModal({
  open,
  onClose,
  onContinue,
  extraction,
  catalogRooms,
  fileName,
  isBlackoutLoading,
}: Props) {
  const matchResults = useMemo<Array<RoomMatchResult>>(
    () => matchRoomsToCatalog(extraction.rooms, catalogRooms),
    [extraction.rooms, catalogRooms],
  );

  const initialConfirmed = useMemo<Record<number, boolean>>(
    () =>
      matchResults.reduce<Record<number, boolean>>((acc, match, index) => {
        acc[index] = match.isExact;
        return acc;
      }, {}),
    [matchResults],
  );

  const [confirmed, setConfirmed] = useState<Record<number, boolean>>(initialConfirmed);
  const [overrides, setOverrides] = useState<Record<number, string>>({});

  const allResolved = matchResults.every((_, index) => confirmed[index]);

  const resolvedCatalogRoomIds = useMemo<Array<string | null>>(
    () => matchResults.map((match, index) => overrides[index] ?? match.catalogRoomId),
    [overrides, matchResults],
  );

  const handleConfirm = useCallback((index: number) => {
    setConfirmed((prev) => ({ ...prev, [index]: true }));
  }, []);

  const handleChange = useCallback((index: number) => {
    setConfirmed((prev) => ({ ...prev, [index]: false }));
    setOverrides((prev) => {
      const next = { ...prev };
      delete next[index];
      return next;
    });
  }, []);

  const handleOverride = useCallback((index: number, catalogRoomId: string) => {
    setOverrides((prev) => ({ ...prev, [index]: catalogRoomId }));
    setConfirmed((prev) => ({ ...prev, [index]: true }));
  }, []);

  const handleContinue = useCallback(() => {
    const resolved: Array<ResolvedRoomMatch> = matchResults.map((match, index) => ({
      extractedName: match.extractedName,
      catalogRoomId: resolvedCatalogRoomIds[index] ?? '',
    }));
    onContinue(resolved);
  }, [matchResults, resolvedCatalogRoomIds, onContinue]);

  const continueDisabled = !allResolved || isBlackoutLoading;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Stack direction="row" alignItems="center" gap={1}>
          <SwapHorizIcon color="primary" />
          <Typography variant="h6">Match Room Names</Typography>
        </Stack>
        <Chip icon={<DescriptionOutlinedIcon />} label={fileName} size="small" sx={{ mt: 1 }} />
      </DialogTitle>

      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Verify that room names from the document match rooms in Admin Portal before applying stopsell dates.
        </Typography>

        <Stack gap={1.5}>
          {matchResults.map((match, index) => (
            <RoomMatchRow
              key={`${match.extractedName}-${index}`}
              match={match}
              index={index}
              catalogRooms={catalogRooms}
              isConfirmed={!!confirmed[index]}
              resolvedCatalogRoomId={resolvedCatalogRoomIds[index]}
              onConfirm={handleConfirm}
              onChange={handleChange}
              onOverride={handleOverride}
            />
          ))}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button variant="text" onClick={onClose}>
          Cancel
        </Button>
        <Tooltip
          title={
            isBlackoutLoading
              ? 'Loading existing blackout dates...'
              : !allResolved
              ? 'Confirm all room matches before continuing'
              : ''
          }
        >
          <span>
            <Button
              variant="contained"
              onClick={handleContinue}
              disabled={continueDisabled}
              startIcon={isBlackoutLoading ? <CircularProgress size={14} color="inherit" /> : undefined}
            >
              Continue to Dates
            </Button>
          </span>
        </Tooltip>
      </DialogActions>
    </Dialog>
  );
}
